import React, { useState, useEffect } from 'react';
import { 
  ComposedChart, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer,
  Line, Bar
} from 'recharts';
import { ProjectGraphData } from '@/types/project';
import { Skeleton } from '@/components/ui/skeleton';

interface GraphDataPoint {
  date: string;
  marketValue?: number;      // Tradeable FDV
  fundingValue?: number;     // Funding FDV  
  secondLaneBuy?: number;    // Buy orders
  secondLaneSell?: number;   // Sell orders
  marketCap?: number;        // Market cap for tooltip
  allBuyOrders?: OrderData[];
  allSellOrders?: OrderData[];
}

interface OrderData {
  id: string;
  fdv: number;
  amount: number;
}

interface ProjectChartProps {
  graphData: ProjectGraphData | null;
  loading?: boolean;
  variant?: 'desktop' | 'mobile';
}

const formatCurrency = (value: number) => {
  const formatScaled = (scaled: number, suffix: string) => {
    const formatted = scaled.toFixed(1).replace(/\.0$/, '');
    return `$ ${formatted}${suffix}`;
  };

  if (value >= 1_000_000_000) return formatScaled(value / 1_000_000_000, 'B');
  if (value >= 1_000_000) return formatScaled(value / 1_000_000, 'M');
  if (value >= 1_000) return formatScaled(value / 1_000, 'K');
  return `$ ${value}`;
};

// Function for processing price history
const processPriceHistory = (
  priceHistory: ProjectGraphData['price_history'] = [],
  dataMap: Map<string, GraphDataPoint>
): Map<string, GraphDataPoint> => {
  if (priceHistory.length) {
    priceHistory.forEach(point => {
      // Only add points with non-zero tradeable FDV values
      if (point.spot_fdv_usd && point.spot_fdv_usd > 0) {
        dataMap.set(point.date, {
          date: point.date,
          marketValue: point.spot_fdv_usd,
          marketCap: point.market_cap_usd // Store market cap for tooltip
        });
      }
    });
  }
  
  return dataMap;
};

// Function for processing funding rounds
const processFundingRounds = (
  fundingRounds: ProjectGraphData['funding_rounds'] = [],
  dataMap: Map<string, GraphDataPoint>
): Map<string, GraphDataPoint> => {
  if (fundingRounds.length) {
    let currentValuation = 0;
    fundingRounds.forEach(round => {
      if (round.valuation !== null) {
        currentValuation = round.valuation;
      }
      
      const entry = dataMap.get(round.date) || { date: round.date };
      entry.fundingValue = currentValuation;
      dataMap.set(round.date, entry);

      // Process fill data
      round.fill?.forEach(fillPoint => {
        const fillEntry = dataMap.get(fillPoint.date) || { date: fillPoint.date };
        fillEntry.fundingValue = fillPoint.valuation ?? currentValuation;
        dataMap.set(fillPoint.date, fillEntry);
      });
    });
  }
  
  return dataMap;
};

// Function for processing orders
const processOrders = (
  orders: ProjectGraphData['orders'] = [],
  dataMap: Map<string, GraphDataPoint>
): Map<string, GraphDataPoint> => {
  const orderMapBuy = new Map<string, number>();
  const orderMapSell = new Map<string, number>();
  const allOrdersMap = new Map<string, { buys: OrderData[], sells: OrderData[] }>();

      if (orders.length) {
      orders.forEach(order => {
        const date = order.date;
        const fdv = order.offered_fully_diluted_valuation;
        const amount = order.offered_amount;
        const id = order.order_id;
        
        // Collecting all orders for each date
        if (!allOrdersMap.has(date)) {
          allOrdersMap.set(date, { buys: [], sells: [] });
        }
        const ordersForDate = allOrdersMap.get(date)!;
        
        if (order.type.includes('BUY') || order.type === 'OrderType.BUY') {
          // For BUY orders we take the minimum FDV value
          if (!orderMapBuy.has(date) || fdv < orderMapBuy.get(date)!) {
            orderMapBuy.set(date, fdv);
          }
          ordersForDate.buys.push({ id, fdv, amount });
        } else if (order.type.includes('SELL') || order.type === 'OrderType.SELL') {
          // For SELL orders we take the maximum FDV value
          if (!orderMapSell.has(date) || fdv > orderMapSell.get(date)!) {
            orderMapSell.set(date, fdv);
          }
          ordersForDate.sells.push({ id, fdv, amount });
        }
      });

    // Apply collected values to dataMap
    for (const [date, fdv] of orderMapBuy.entries()) {
      const entry = dataMap.get(date) || { date };
      entry.secondLaneBuy = fdv;
      entry.allBuyOrders = allOrdersMap.get(date)?.buys;
      dataMap.set(date, entry);
    }

    for (const [date, fdv] of orderMapSell.entries()) {
      const entry = dataMap.get(date) || { date };
      entry.secondLaneSell = fdv;
      entry.allSellOrders = allOrdersMap.get(date)?.sells;
      dataMap.set(date, entry);
    }
  }
  
  return dataMap;
};

// Function for sorting data by date
const sortDataByDate = (dataMap: Map<string, GraphDataPoint>): GraphDataPoint[] => {
  return Array.from(dataMap.values())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

const ProjectChart: React.FC<ProjectChartProps> = ({ graphData, loading = false, variant = 'desktop' }) => {
  const isMobile = variant === 'mobile';
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });
  const [chartData, setChartData] = useState<GraphDataPoint[]>([]);
  const [hasFundingValuation, setHasFundingValuation] = useState(false);
  const [shouldShowChart, setShouldShowChart] = useState(true);
  const chartContainerClassName = isMobile
    ? 'bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] h-[465px] px-0 pt-[16px] pb-6'
    : 'bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] h-[465px] px-8 pt-8 pb-10';
  const spotFdvPointsCount = chartData.reduce((count, point) => (
    typeof point.marketValue === 'number' && point.marketValue > 0 ? count + 1 : count
  ), 0);

  // Note: All values on the chart (Y-axis) are represented in FDV (Fully Diluted Valuation)
  // The black line represents market cap (from market_cap_usd)
  // The red line represents funding rounds valuation
  // The green bars represent SecondLane Buy orders
  // The hatched bars represent SecondLane Sell orders
  
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const updateTheme = () => {
      setIsDark(root.classList.contains('dark'));
    };

    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!graphData) {
      setShouldShowChart(false);
      return;
    }



    // Check if we should show the chart
    const hasOrders = graphData.orders && graphData.orders.length > 0;
    const hasPriceHistory = graphData.price_history && graphData.price_history.length > 0;
    
    // Check if any funding round has FDV and it's not zero
    let anyFundingRoundHasFDV = false;
    if (graphData.funding_rounds && graphData.funding_rounds.length > 0) {
      anyFundingRoundHasFDV = graphData.funding_rounds.some(round => 
        round.valuation !== null && round.valuation > 0
      );
      setHasFundingValuation(anyFundingRoundHasFDV);
    }

    // Check if price history has non-zero values
    let hasNonZeroPriceHistory = false;
    if (hasPriceHistory) {
      hasNonZeroPriceHistory = graphData.price_history!.some(
        item => item.spot_fdv_usd > 0
      );
    }

    // Check if orders have non-zero values
    let hasNonZeroOrders = false;
    if (hasOrders) {
      hasNonZeroOrders = graphData.orders!.some(
        order => order.offered_fully_diluted_valuation > 0
      );
    }

    // If there are funding rounds but no FDV or only zero FDVs, no orders or only zero orders, 
    // and no price history or only zero market caps, we should not show the chart
    if ((graphData.funding_rounds && 
        graphData.funding_rounds.length > 0 && 
        !anyFundingRoundHasFDV) && 
        (!hasOrders || !hasNonZeroOrders) && 
        (!hasPriceHistory || !hasNonZeroPriceHistory)) {
      setShouldShowChart(false);
      return;
    }

    // Initialization of dataMap for storing chart data
    let dataMap = new Map<string, GraphDataPoint>();

    // Processing different types of data
    dataMap = processPriceHistory(graphData.price_history, dataMap);
    dataMap = processFundingRounds(graphData.funding_rounds, dataMap);
    dataMap = processOrders(graphData.orders, dataMap);

    // Sorting data by date
    const sortedData = sortDataByDate(dataMap);

    setChartData(sortedData);
  }, [graphData]);

  if (loading) {
    return (
      <div className={chartContainerClassName}>
        <Skeleton className="h-[340px] w-full" />
      </div>
    );
  }

  if (!shouldShowChart || !chartData.length) {
    return (
      <div className={chartContainerClassName}>
        <div className="text-center py-8">
          <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            No chart data available for this project
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={chartContainerClassName}>
      <div className={`w-full ${isMobile ? 'h-[350px]' : 'h-[350px]'}`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart 
            data={chartData}
            margin={isMobile ? { top: 8, right: 8, left: -18, bottom: 0 } : { top: 8, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <pattern id="sellPattern" width="6" height="6" patternUnits="userSpaceOnUse">
                <rect width="6" height="6" fill="rgba(107,105,105,0.32)" />
                <path d="M0 6 L6 0" stroke="#6B6969" strokeWidth="1" />
              </pattern>
            </defs>
            <CartesianGrid 
              vertical={false} 
              horizontal={true} 
              stroke={isDark ? 'rgba(253,253,253,0.12)' : 'rgba(45,41,35,0.16)'}
              strokeDasharray="4 4"
            />
            <XAxis 
              dataKey="date" 
              tick={{ 
                fontSize: isMobile ? 12 : 12,
                fontWeight: 500,
                fill: isDark ? 'rgba(253,253,253,0.64)' : 'rgba(45,41,35,0.72)',
                fontFamily: "Plus Jakarta Sans"
              }}
              tickMargin={isMobile ? 10 : 10}
              stroke="transparent"
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              tick={{ 
                fontSize: 13, 
                fontWeight: 500,
                fill: isDark ? 'rgba(253,253,253,0.64)' : 'rgba(45,41,35,0.72)',
                fontFamily: "Plus Jakarta Sans"
              }}
              width={isMobile ? 58 : 66}
              stroke="transparent"
              axisLine={false}
              tickLine={false}
            />
            
            <Tooltip 
              contentStyle={{
                backgroundColor: isDark ? '#1c1c1c' : '#fff',
                border: isDark ? '1px solid #171717' : '1px solid rgba(45,41,35,0.12)',
                color: isDark ? '#fdfdfd' : '#2d2923',
                borderRadius: "0",
                fontFamily: "Plus Jakarta Sans"
              }}
              formatter={(value: number, name: string, entry: {payload?: GraphDataPoint}) => {
                const formattedValue = formatCurrency(value);
                const labels = {
                  marketValue: 'Tradeable FDV',
                  fundingValue: 'Funding FDV',
                  secondLaneBuy: 'SecondLane Buy',
                  secondLaneSell: 'SecondLane Sell'
                };
                
                if (name === 'marketValue') {
                  return [formattedValue, labels[name as keyof typeof labels]];
                }
                
                if (entry?.payload) {
                  const data = entry.payload;
                  
                  if (name === 'secondLaneBuy' && data.allBuyOrders && data.allBuyOrders.length > 1) {
                    const allOrders = data.allBuyOrders.map((order) => 
                      `- ${formatCurrency(order.fdv)} (${formatCurrency(order.amount)})`
                    ).join('\n');
                    return [`${formattedValue}\nAll Buy Orders:\n${allOrders}`, labels[name as keyof typeof labels]];
                  }
                  
                  if (name === 'secondLaneSell' && data.allSellOrders && data.allSellOrders.length > 1) {
                    const allOrders = data.allSellOrders.map((order) => 
                      `- ${formatCurrency(order.fdv)} (${formatCurrency(order.amount)})`
                    ).join('\n');
                    return [`${formattedValue}\nAll Sell Orders:\n${allOrders}`, labels[name as keyof typeof labels]];
                  }
                }
                
                return [formattedValue, labels[name as keyof typeof labels]];
              }}
              labelFormatter={(label) => `Date: ${label}`}
            />
            
            {/* Tradeable FDV - Green/Lime line */}
            <Line
              type="monotone"
              dataKey="marketValue"
              stroke="#a9c804"
              strokeWidth={2}
              dot={spotFdvPointsCount === 1 ? { r: 4, fill: '#a9c804', stroke: '#a9c804' } : false}
              connectNulls
            />
            
            {/* Funding FDV - Orange line */}
            {hasFundingValuation && (
              <Line
                type="stepAfter"
                dataKey="fundingValue"
                stroke="#ee9d1a"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            )}
            
            {/* SecondLane Buy */}
            <Bar
              dataKey="secondLaneBuy"
              fill="#3fc8e4"
              barSize={isMobile ? 12 : 14}
            />
            
            {/* SecondLane Sell */}
            <Bar
              dataKey="secondLaneSell"
              fill="url(#sellPattern)"
              stroke="#6b6969"
              strokeWidth={1}
              barSize={isMobile ? 12 : 14}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Legend */}
      <div className={isMobile ? 'mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 px-3' : 'mt-6 flex flex-wrap justify-center gap-8'}>
        <div className={isMobile ? 'flex items-center gap-2' : 'flex items-center gap-3'}>
          <div className="w-[19px] h-[8px] bg-[#a9c804]" />
          <span className={`font-['Plus_Jakarta_Sans'] font-medium ${isMobile ? 'text-[14px] leading-[20px]' : 'text-[14px] leading-[20px]'} text-[#2d2923] dark:text-[#fdfdfd]`}>
            Tradeable FDV
          </span>
        </div>
        {hasFundingValuation && (
          <div className={isMobile ? 'flex items-center gap-2' : 'flex items-center gap-3'}>
            <div className="w-[19px] h-[8px] bg-[#ee9d1a]" />
            <span className={`font-['Plus_Jakarta_Sans'] font-medium ${isMobile ? 'text-[14px] leading-[20px]' : 'text-[14px] leading-[20px]'} text-[#2d2923] dark:text-[#fdfdfd]`}>
              Funding FDV
            </span>
          </div>
        )}
        <div className={isMobile ? 'flex items-center gap-2' : 'flex items-center gap-3'}>
          <div className="w-[19px] h-[8px] bg-[#3fc8e4]" />
          <span className={`font-['Plus_Jakarta_Sans'] font-medium ${isMobile ? 'text-[14px] leading-[20px]' : 'text-[14px] leading-[20px]'} text-[#2d2923] dark:text-[#fdfdfd]`}>
            SecondLane Buy
          </span>
        </div>
        <div className={isMobile ? 'flex items-center gap-2' : 'flex items-center gap-3'}>
          <div className="w-[19px] h-[8px] border border-dashed border-[#6b6969] bg-[rgba(107,105,105,0.32)]" />
          <span className={`font-['Plus_Jakarta_Sans'] font-medium ${isMobile ? 'text-[14px] leading-[20px]' : 'text-[14px] leading-[20px]'} text-[#2d2923] dark:text-[#fdfdfd]`}>
            SecondLane Sell
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProjectChart;
